import React, { Component } from 'react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { Mail, Loader2, Sparkles, CheckCircle, AlertTriangle, Search, ChevronDown, Check, FileEdit, Save, X, AlertCircle } from 'lucide-react';
import s from './style.css';

const LANGUAGES = [
  { code: 'es', name: 'Español', isBase: true },
  { code: 'en-US', name: 'English', isBase: false },
  { code: 'fr-FR', name: 'Français', isBase: false },
  { code: 'it-IT', name: 'Italiano', isBase: false },
  { code: 'pt-PT', name: 'Português', isBase: false },
];

const FIELDS = ['subject', 'heading', 'content', 'buttonText'];

// Variables that should NOT be translated
const PROTECTED_VARIABLES = [
  'name', 'userName', 'email', 'hostName', 'guestName', 'senderName',
  'receiverName', 'confirmationCode', 'listTitle', 'checkIn', 'checkOut',
  'basePrice', 'total', 'currency', 'siteName', 'threadId', 'hostEmail',
  'guestEmail', 'listId', 'reservationId', 'message', 'phoneNumber',
  'payoutAmount', 'hostServiceFee', 'guestServiceFee', 'nights', 'guests'
];

class SmartEmailTemplateEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      templates: {},           // { templateType: { locale: { subject, heading, content, buttonText } } }
      originalTemplates: {},   // For tracking modifications
      expandedTemplates: {},   // { templateType: true/false }
      modifiedTemplates: new Set(),
      loading: true,
      saving: false,
      translatingTemplate: null,
      bulkTranslating: false,
      bulkProgress: { current: 0, total: 0 },
      forceOverwrite: false,
      searchTerm: '',
      filterMode: 'all',
      successMessage: '',
      errorMessage: '',
    };
  }

  componentDidMount() {
    this.loadTemplates();
  }

  async loadTemplates() {
    try {
      const query = `
        query {
          getEmailTemplates {
            id
            templateType
            locale
            subject
            heading
            content
            buttonText
          }
        }
      `;

      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query }),
      });

      const { data, errors } = await response.json();
      
      if (errors) {
        console.error('GraphQL errors:', errors);
        this.setState({ loading: false, errorMessage: 'Failed to load templates' });
        return;
      }

      // Group templates by templateType
      const rawTemplates = data.getEmailTemplates || [];
      const templates = {};
      
      rawTemplates.forEach(t => {
        if (!templates[t.templateType]) {
          templates[t.templateType] = {};
        }
        templates[t.templateType][t.locale] = {
          id: t.id,
          subject: t.subject || '',
          heading: t.heading || '',
          content: t.content || '',
          buttonText: t.buttonText || '',
        };
      });

      // Deep copy for original
      const originalTemplates = JSON.parse(JSON.stringify(templates));

      this.setState({ 
        templates, 
        originalTemplates,
        loading: false 
      });
    } catch (e) {
      console.error('Failed to load templates:', e);
      this.setState({ loading: false, errorMessage: e.message });
    }
  }

  handleFieldChange = (templateType, locale, field, value) => {
    const { templates, modifiedTemplates } = this.state;
    
    // Ensure structure exists
    if (!templates[templateType]) {
      templates[templateType] = {};
    }
    if (!templates[templateType][locale]) {
      templates[templateType][locale] = { subject: '', heading: '', content: '', buttonText: '' };
    }
    
    templates[templateType][locale][field] = value;
    modifiedTemplates.add(`${templateType}:${locale}`);
    
    this.setState({ templates: { ...templates }, modifiedTemplates: new Set(modifiedTemplates) });
  }

  toggleExpand = (templateType) => {
    const { expandedTemplates } = this.state;
    this.setState({
      expandedTemplates: {
        ...expandedTemplates,
        [templateType]: !expandedTemplates[templateType]
      }
    });
  }

  // Extract variables from text and replace with placeholders
  extractVariables(text) {
    const regex = /\{([^}]+)\}/g;
    const variables = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      variables.push({ original: match[0], name: match[1] });
    }
    
    let processedText = text;
    variables.forEach((v, i) => {
      processedText = processedText.replace(v.original, `__VAR_${i}__`);
    });
    
    return { processedText, variables };
  }

  // Restore variables after translation
  restoreVariables(text, variables) {
    let restoredText = text;
    variables.forEach((v, i) => {
      restoredText = restoredText.replace(`__VAR_${i}__`, v.original);
    });
    return restoredText;
  }

  handleTranslateTemplate = async (templateType) => {
    const { templates, forceOverwrite, modifiedTemplates } = this.state;
    const baseTemplate = templates[templateType]?.['es'];
    
    if (!baseTemplate) {
      this.setState({ errorMessage: `No Spanish base template for ${templateType}` });
      return;
    }

    this.setState({ translatingTemplate: templateType, errorMessage: '' });

    try {
      const targetLangs = LANGUAGES.filter(l => !l.isBase).map(l => l.code);
      
      for (const field of FIELDS) {
        const baseText = baseTemplate[field];
        if (!baseText || baseText.trim() === '') continue;

        // Check if we should translate this field for each language
        const langsToTranslate = targetLangs.filter(lang => {
          const existing = templates[templateType]?.[lang]?.[field];
          return forceOverwrite || !existing || existing.trim() === '';
        });

        if (langsToTranslate.length === 0) continue;

        // Extract variables before translation
        const { processedText, variables } = this.extractVariables(baseText);

        // Call translation API
        const response = await fetch('/web-api/admin/translate-ui-message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sourceText: processedText,
            sourceLang: 'es',
            targetLangs: langsToTranslate.map(l => l.split('-')[0]), // en-US -> en
          }),
        });

        const result = await response.json();
        
        if (result.success) {
          // Apply translations with restored variables
          langsToTranslate.forEach(lang => {
            const langCode = lang.split('-')[0]; // en-US -> en
            let translated = result.translations[langCode];
            if (translated) {
              translated = this.restoreVariables(translated, variables);
              
              if (!templates[templateType][lang]) {
                templates[templateType][lang] = { subject: '', heading: '', content: '', buttonText: '' };
              }
              templates[templateType][lang][field] = translated;
              modifiedTemplates.add(`${templateType}:${lang}`);
            }
          });
        }
      }

      this.setState({ 
        templates: { ...templates }, 
        modifiedTemplates: new Set(modifiedTemplates),
        translatingTemplate: null,
        successMessage: `Template "${templateType}" translated successfully!`
      });

      setTimeout(() => this.setState({ successMessage: '' }), 3000);
    } catch (e) {
      console.error('Translation error:', e);
      this.setState({ 
        translatingTemplate: null, 
        errorMessage: `Translation failed: ${e.message}` 
      });
    }
  }

  handleBulkTranslate = async () => {
    const { templates, forceOverwrite } = this.state;
    const templateTypes = Object.keys(templates);
    
    this.setState({ 
      bulkTranslating: true, 
      bulkProgress: { current: 0, total: templateTypes.length } 
    });

    for (let i = 0; i < templateTypes.length; i++) {
      const templateType = templateTypes[i];
      
      // Check if this template needs translation
      const needsTranslation = LANGUAGES.some(lang => {
        if (lang.isBase) return false;
        return FIELDS.some(field => {
          const existing = templates[templateType]?.[lang.code]?.[field];
          const base = templates[templateType]?.['es']?.[field];
          return base && (forceOverwrite || !existing || existing.trim() === '');
        });
      });

      if (needsTranslation) {
        await this.handleTranslateTemplate(templateType);
      }

      this.setState({ bulkProgress: { current: i + 1, total: templateTypes.length } });
    }

    this.setState({ 
      bulkTranslating: false,
      successMessage: 'All templates translated!'
    });
    
    setTimeout(() => this.setState({ successMessage: '' }), 3000);
  }

  handleSaveAll = async () => {
    const { templates, modifiedTemplates } = this.state;
    
    if (modifiedTemplates.size === 0) return;
    
    this.setState({ saving: true, errorMessage: '' });

    try {
      // Group modifications by template
      const updates = [];
      
      modifiedTemplates.forEach(key => {
        const [templateType, locale] = key.split(':');
        const data = templates[templateType]?.[locale];
        if (data) {
          updates.push({
            templateType,
            locale,
            ...data
          });
        }
      });

      // Save each modified template
      for (const update of updates) {
        const mutation = `
          mutation updateEmailTemplate(
            $templateType: String!,
            $locale: String!,
            $subject: String!,
            $heading: String,
            $content: String!,
            $buttonText: String
          ) {
            updateEmailTemplate(
              templateType: $templateType,
              locale: $locale,
              subject: $subject,
              heading: $heading,
              content: $content,
              buttonText: $buttonText
            ) {
              status
            }
          }
        `;

        const response = await fetch('/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            query: mutation,
            variables: update
          }),
        });

        const result = await response.json();
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
      }

      // Update original templates and clear modifications
      const originalTemplates = JSON.parse(JSON.stringify(templates));
      
      this.setState({ 
        saving: false,
        modifiedTemplates: new Set(),
        originalTemplates,
        successMessage: `Saved ${updates.length} template(s) successfully!`
      });

      setTimeout(() => this.setState({ successMessage: '' }), 3000);
    } catch (e) {
      console.error('Save error:', e);
      this.setState({ 
        saving: false, 
        errorMessage: `Save failed: ${e.message}` 
      });
    }
  }

  handleDiscard = () => {
    const { originalTemplates } = this.state;
    this.setState({
      templates: JSON.parse(JSON.stringify(originalTemplates)),
      modifiedTemplates: new Set(),
    });
  }

  getMissingCount() {
    const { templates } = this.state;
    let count = 0;
    
    Object.keys(templates).forEach(templateType => {
      LANGUAGES.forEach(lang => {
        if (lang.isBase) return;
        FIELDS.forEach(field => {
          const value = templates[templateType]?.[lang.code]?.[field];
          const baseValue = templates[templateType]?.['es']?.[field];
          if (baseValue && (!value || value.trim() === '')) count++;
        });
      });
    });
    
    return count;
  }

  getFilteredTemplates() {
    const { templates, searchTerm, filterMode } = this.state;
    let templateTypes = Object.keys(templates).sort();
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      templateTypes = templateTypes.filter(type => {
        if (type.toLowerCase().includes(term)) return true;
        // Search in content
        return LANGUAGES.some(lang => {
          return FIELDS.some(field => {
            const val = templates[type]?.[lang.code]?.[field];
            return val && val.toLowerCase().includes(term);
          });
        });
      });
    }
    
    // Mode filter
    if (filterMode === 'missing') {
      templateTypes = templateTypes.filter(type => {
        return LANGUAGES.some(lang => {
          if (lang.isBase) return false;
          return FIELDS.some(field => {
            const val = templates[type]?.[lang.code]?.[field];
            const base = templates[type]?.['es']?.[field];
            return base && (!val || val.trim() === '');
          });
        });
      });
    }
    
    return templateTypes;
  }

  getTemplateStatus(templateType) {
    const { templates } = this.state;
    let missing = 0;
    let total = 0;
    
    LANGUAGES.forEach(lang => {
      if (lang.isBase) return;
      FIELDS.forEach(field => {
        const base = templates[templateType]?.['es']?.[field];
        if (base && base.trim()) {
          total++;
          const val = templates[templateType]?.[lang.code]?.[field];
          if (!val || val.trim() === '') missing++;
        }
      });
    });
    
    return { missing, total };
  }

  render() {
    const {
      templates,
      expandedTemplates,
      modifiedTemplates,
      loading,
      saving,
      translatingTemplate,
      bulkTranslating,
      bulkProgress,
      forceOverwrite,
      searchTerm,
      filterMode,
      successMessage,
      errorMessage,
    } = this.state;
    
    if (loading) {
      return (
        <div className={s.root}>
          <div className={s.loadingSpinner}>Loading email templates...</div>
        </div>
      );
    }
    
    const filteredTemplates = this.getFilteredTemplates();
    const missingCount = this.getMissingCount();
    const totalTemplates = Object.keys(templates).length;

    return (
      <div className={s.root}>
        {/* Header */}
        <div className={s.header}>
          <h1 className={s.title}><Mail size={28} className={s.titleIcon} /> Smart Email Template Editor</h1>
          <div className={s.headerActions}>
            <label 
              className={`${s.forceOverwrite} ${forceOverwrite ? s.active : ''}`}
              title="When checked, will overwrite existing translations"
            >
              <input
                type="checkbox"
                checked={forceOverwrite}
                onChange={(e) => this.setState({ forceOverwrite: e.target.checked })}
              />
              Force Overwrite
            </label>
            <button
              className={`${s.bulkButton} ${s.primary}`}
              onClick={this.handleBulkTranslate}
              disabled={bulkTranslating || missingCount === 0 && !forceOverwrite}
            >
              {bulkTranslating 
                ? <><Loader2 size={18} className={s.spinning} /> Translating {bulkProgress.current}/{bulkProgress.total}...</>
                : <><Sparkles size={18} /> Translate All {forceOverwrite ? '(Force)' : `(${missingCount} missing)`}</>
              }
            </button>
          </div>
        </div>

        {/* Messages */}
        {successMessage && <div className={s.successMessage}><CheckCircle size={18} /> {successMessage}</div>}
        {errorMessage && <div className={s.errorMessage}><AlertCircle size={18} /> {errorMessage}</div>}
        
        {/* Bulk Progress */}
        {bulkTranslating && (
          <div className={s.progressBar}>
            <div 
              className={s.progressFill} 
              style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
            />
          </div>
        )}

        {/* Stats Bar */}
        <div className={s.statsBar}>
          <div className={s.statItem}>
            <span className={s.statValue}>{totalTemplates}</span>
            <span className={s.statLabel}>Templates</span>
          </div>
          <div className={s.statItem}>
            <span className={s.statValue} style={{ color: missingCount > 0 ? '#ffd54f' : '#a5d6a7', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {missingCount > 0 ? <><AlertTriangle size={18} /> {missingCount}</> : <><CheckCircle size={18} /> 0</>}
            </span>
            <span className={s.statLabel}>Missing Fields</span>
          </div>
          <div className={s.statItem}>
            <span className={s.statValue}>{modifiedTemplates.size}</span>
            <span className={s.statLabel}>Modified</span>
          </div>
          <div className={s.statItem}>
            <span className={s.statValue}>{filteredTemplates.length}</span>
            <span className={s.statLabel}>Showing</span>
          </div>
        </div>

        {/* Search & Filters */}
        <div className={s.searchSection}>
          <input
            type="text"
            className={s.searchInput}
            placeholder="Search by template name or content..."
            value={searchTerm}
            onChange={(e) => this.setState({ searchTerm: e.target.value })}
          />
          <select
            className={s.filterSelect}
            value={filterMode}
            onChange={(e) => this.setState({ filterMode: e.target.value })}
          >
            <option value="all">All ({totalTemplates})</option>
            <option value="missing">Missing ({this.getFilteredTemplates().length})</option>
          </select>
          <button
            className={s.findMissingBtn}
            onClick={() => this.setState({ filterMode: 'missing' })}
            disabled={missingCount === 0}
            style={{
              background: missingCount > 0 
                ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' 
                : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
            }}
          >
            {missingCount > 0 ? <><Search size={16} /> Find Missing ({missingCount})</> : <><CheckCircle size={16} /> All Complete!</>}
          </button>
        </div>

        {/* Template Cards */}
        {filteredTemplates.map(templateType => {
          const isExpanded = expandedTemplates[templateType];
          const status = this.getTemplateStatus(templateType);
          const isTranslating = translatingTemplate === templateType;
          
          return (
            <div key={templateType} className={s.templateCard}>
              <div 
                className={s.templateHeader}
                onClick={() => this.toggleExpand(templateType)}
              >
                <div className={s.templateTitle}>
                  <ChevronDown size={18} className={`${s.expandIcon} ${isExpanded ? s.expanded : ''}`} />
                  {templateType}
                  <span className={`${s.templateBadge} ${status.missing > 0 ? s.badgeMissing : s.badgeComplete}`}>
                    {status.missing > 0 ? `${status.missing} missing` : <><Check size={14} /> Complete</>}
                  </span>
                </div>
                <div className={s.templateActions}>
                  <button
                    className={s.translateBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.handleTranslateTemplate(templateType);
                    }}
                    disabled={isTranslating || (status.missing === 0 && !forceOverwrite)}
                  >
                    {isTranslating ? <><Loader2 size={14} className={s.spinning} /> Translating...</> : <><Sparkles size={14} /> AI Translate</>}
                  </button>
                </div>
              </div>
              
              <div className={`${s.templateBody} ${isExpanded ? s.expanded : ''}`}>
                {/* Language Headers */}
                <div className={s.fieldRow}>
                  <div className={s.fieldLabel}></div>
                  {LANGUAGES.map(lang => (
                    <div 
                      key={lang.code} 
                      className={`${s.langHeader} ${lang.isBase ? s.base : ''}`}
                    >
                      <span className={s.langBadge}>{lang.code.split('-')[0].toUpperCase()}</span> {lang.isBase && <span className={s.baseBadge}>BASE</span>}
                    </div>
                  ))}
                </div>
                
                {/* Field Rows */}
                <div className={s.fieldsGrid}>
                  {FIELDS.map(field => (
                    <div key={field} className={s.fieldRow}>
                      <div className={s.fieldLabel}>{field}</div>
                      {LANGUAGES.map(lang => {
                        const value = templates[templateType]?.[lang.code]?.[field] || '';
                        const baseValue = templates[templateType]?.['es']?.[field] || '';
                        const isMissing = !lang.isBase && baseValue && !value.trim();
                        const isModified = modifiedTemplates.has(`${templateType}:${lang.code}`);
                        
                        return (
                          <div key={lang.code}>
                            {field === 'content' ? (
                              <textarea
                                className={`${s.fieldInput} ${lang.isBase ? s.base : ''} ${isMissing ? s.missing : ''} ${isModified ? s.modified : ''}`}
                                value={value}
                                onChange={(e) => this.handleFieldChange(templateType, lang.code, field, e.target.value)}
                                rows={4}
                                placeholder={lang.isBase ? 'Enter base content...' : 'Translation...'}
                              />
                            ) : (
                              <input
                                type="text"
                                className={`${s.fieldInput} ${lang.isBase ? s.base : ''} ${isMissing ? s.missing : ''} ${isModified ? s.modified : ''}`}
                                value={value}
                                onChange={(e) => this.handleFieldChange(templateType, lang.code, field, e.target.value)}
                                placeholder={lang.isBase ? 'Enter base text...' : 'Translation...'}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                
                {/* Variables Hint */}
                <div className={s.variableHint}>
                  <strong>Protected variables (will not be translated):</strong>{' '}
                  {PROTECTED_VARIABLES.slice(0, 10).map(v => (
                    <span key={v} className={s.variableTag}>{`{${v}}`}</span>
                  ))}
                  {PROTECTED_VARIABLES.length > 10 && <span> +{PROTECTED_VARIABLES.length - 10} more</span>}
                </div>
              </div>
            </div>
          );
        })}

        {/* Save Bar */}
        <div className={`${s.saveBar} ${modifiedTemplates.size > 0 ? s.visible : ''}`}>
          <div className={s.saveInfo}>
            <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}><FileEdit size={18} /> {modifiedTemplates.size} template(s) modified</span>
          </div>
          <div className={s.saveActions}>
            <button 
              className={s.saveButton}
              onClick={this.handleSaveAll}
              disabled={saving}
            >
              {saving ? <><Loader2 size={16} className={s.spinning} /> Saving...</> : <><Save size={16} /> Save All Changes</>}
            </button>
            <button 
              className={s.discardButton}
              onClick={this.handleDiscard}
              disabled={saving}
            >
              <X size={16} /> Discard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(SmartEmailTemplateEditor);
